/**
 * EN Cart Drawer — controller
 * ---------------------------------------------------------------------------
 * Drives sections/en-cart-drawer.liquid, mounted from layout/theme.liquid.
 *
 * Refresh strategy: every cart mutation re-fetches the whole section from the
 * Section Rendering API and swaps [data-cart-contents]. Liquid renders all
 * money, discounts and empty states, so there is no client-side formatting and
 * nothing can drift out of sync with the real cart.
 *
 * The shell (#CartDrawer) and header are deliberately OUTSIDE the swapped
 * region: theme.Drawers holds a direct reference to #CartDrawer and binds
 * .js-drawer-close once, so replacing either would silently break open/close.
 *
 * Subscriptions: PayWhirl issues native Shopify selling plans, so upgrading a
 * line is a plain /cart/change.js call carrying selling_plan. No PayWhirl file
 * is touched, and the plan id comes from the rendered markup.
 *
 * Recommendations: fetched from /recommendations/products with a section_id, so
 * Shopify returns rendered HTML rather than JSON and Liquid still owns the
 * money formatting. Seeded from the most recently added line. With an empty
 * cart there is no seed, and the section renders a fixed fallback inline
 * instead — so this file does nothing in that case.
 *
 * Progressive enhancement: +/-/remove are real links to /cart/change and work
 * with JS off. Subscribe and Add require JS by necessity, since both need POSTs.
 */
(function () {
  'use strict';

  var SECTION_ID = 'en-cart-drawer';
  var REC_SECTION_ID = 'en-cart-recommendations';
  var REC_INTENT = 'related';
  var REC_LIMIT = 10;

  var SELECTORS = {
    drawer: '#CartDrawer',
    contents: '[data-cart-contents]',
    status: '[data-cart-status]',
    error: '[data-cart-error]',
    count: '[data-cart-count]',
    close: '[data-cart-close]',
    change: '[data-qty-up], [data-qty-down], [data-cart-remove]',
    subscribe: '[data-cart-subscribe]',
    plan: '[data-cart-plan]',
    unsubscribe: '[data-cart-unsubscribe]',
    item: '[data-cart-item]',
    qtyNum: '[data-qty-num]',
    rec: '[data-cart-recommendations]',
    recTrack: '[data-cart-rec-track]',
    recPrev: '[data-cart-rec-prev]',
    recNext: '[data-cart-rec-next]',
    recAdd: '[data-cart-rec-add]',
    recCard: '.en-cd__rec-card',
    bubble: '.cart-link__bubble',
    bubbleNum: '.cart-link__bubble-num'
  };

  var CLASSES = {
    busy: 'en-cd--busy',
    bubbleVisible: 'cart-link__bubble--visible'
  };

  var JSON_HEADERS = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  };

  var busy = false;

  var GENERIC_CART_ERROR =
    'Sorry, we could not update your basket. Please try again.';

  /* --- helpers ----------------------------------------------------------- */

  function getDrawer() {
    return document.querySelector(SELECTORS.drawer);
  }

  function rootUrl() {
    var root = (theme.routes && theme.routes.home) ? theme.routes.home : '/';
    if (root.charAt(root.length - 1) !== '/') {
      root += '/';
    }
    return root;
  }

  function sectionUrl() {
    return rootUrl() + '?sections=' + SECTION_ID;
  }

  function recommendationsUrl(productId) {
    return rootUrl() + 'recommendations/products' +
      '?section_id=' + REC_SECTION_ID +
      '&product_id=' + encodeURIComponent(productId) +
      '&intent=' + REC_INTENT +
      '&limit=' + REC_LIMIT;
  }

  function setBusy(state) {
    busy = state;
    var el = getDrawer();
    if (!el) {
      return;
    }
    if (state) {
      el.classList.add(CLASSES.busy);
    } else {
      el.classList.remove(CLASSES.busy);
    }
  }

  /**
   * Read line and quantity straight off the link's href. Liquid has already
   * calculated both, so there is no arithmetic to get wrong here and the
   * numbers cannot disagree with what the server rendered.
   */
  function parseChangeHref(href) {
    if (!href) {
      return null;
    }
    var url = new URL(href, window.location.origin);
    var line = parseInt(url.searchParams.get('line'), 10);
    var quantity = parseInt(url.searchParams.get('quantity'), 10);
    if (isNaN(line) || isNaN(quantity)) {
      return null;
    }
    return { line: line, quantity: quantity };
  }

  function updateHeaderBubble(count) {
    if (count === null || isNaN(count)) {
      return;
    }

    document.querySelectorAll(SELECTORS.bubbleNum).forEach(function (el) {
      el.innerText = count;
    });

    document.querySelectorAll(SELECTORS.bubble).forEach(function (el) {
      if (count > 0) {
        el.classList.add(CLASSES.bubbleVisible);
      } else {
        el.classList.remove(CLASSES.bubbleVisible);
      }
    });
  }

  /* --- recommendations --------------------------------------------------- */

  function cardStep(track) {
    var card = track.querySelector(SELECTORS.recCard);
    if (!card) {
      return track.clientWidth;
    }
    var styles = window.getComputedStyle(track);
    var gap = parseInt(styles.columnGap || styles.gap, 10);
    if (isNaN(gap)) {
      gap = 0;
    }
    return card.getBoundingClientRect().width + gap;
  }

  function updateArrows(container) {
    if (!container) {
      return;
    }

    var track = container.querySelector(SELECTORS.recTrack);
    if (!track) {
      return;
    }

    var prev = container.querySelector(SELECTORS.recPrev);
    var next = container.querySelector(SELECTORS.recNext);
    var maxScroll = track.scrollWidth - track.clientWidth - 1;

    // Nothing to scroll to — a single card, or cards that already fit. Hide the
    // arrows entirely rather than showing two permanently dead controls.
    if (maxScroll <= 0) {
      if (prev) {
        prev.setAttribute('hidden', '');
      }
      if (next) {
        next.setAttribute('hidden', '');
      }
      return;
    }

    if (prev) {
      prev.removeAttribute('hidden');
      prev.disabled = track.scrollLeft <= 0;
    }
    if (next) {
      next.removeAttribute('hidden');
      next.disabled = track.scrollLeft >= maxScroll;
    }
  }

  /**
   * Track is replaced on every refresh, so the scroll listener is attached to
   * the new node each time and flagged to avoid stacking duplicates.
   */
  function bindTrack(container) {
    if (!container) {
      return;
    }

    var track = container.querySelector(SELECTORS.recTrack);
    if (!track || track.getAttribute('data-track-bound') === 'true') {
      return;
    }

    track.setAttribute('data-track-bound', 'true');
    track.addEventListener('scroll', function () {
      updateArrows(track.closest(SELECTORS.rec));
    });
  }

  function loadRecommendations() {
    var container = document.querySelector(SELECTORS.rec);
    if (!container) {
      return Promise.resolve();
    }

    var seed = container.getAttribute('data-seed');

    // No seed means an empty cart: the section rendered the fixed fallback
    // inline, so there is nothing to fetch.
    if (!seed) {
      bindTrack(container);
      updateArrows(container);
      return Promise.resolve();
    }

    return fetch(recommendationsUrl(seed), { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Recommendations fetch failed: ' + response.status);
        }
        return response.text();
      })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var wrapper = parsed.getElementById('shopify-section-' + REC_SECTION_ID);
        var markup = wrapper ? wrapper.innerHTML : (parsed.body ? parsed.body.innerHTML : '');

        if (!markup || !markup.trim()) {
          container.innerHTML = '';
          container.setAttribute('hidden', '');
          return;
        }

        container.innerHTML = markup;
        container.removeAttribute('hidden');
        bindTrack(container);
        updateArrows(container);
      })
      .catch(function (error) {
        // Leave the slot hidden rather than showing an empty band.
        console.error('[en-cart-drawer]', error);
      });
  }

  /* --- core -------------------------------------------------------------- */

  function refresh() {
    return fetch(sectionUrl(), { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Section fetch failed: ' + response.status);
        }
        return response.json();
      })
      .then(function (data) {
        var html = data[SECTION_ID];
        if (!html) {
          throw new Error('Section markup missing from response');
        }

        var parsed = new DOMParser().parseFromString(html, 'text/html');

        var freshContents = parsed.querySelector(SELECTORS.contents);
        var liveContents = document.querySelector(SELECTORS.contents);
        if (!freshContents || !liveContents) {
          throw new Error('Cart contents container missing');
        }

        liveContents.innerHTML = freshContents.innerHTML;

        var freshCount = parsed.querySelector(SELECTORS.count);
        var liveCount = document.querySelector(SELECTORS.count);
        var count = null;

        if (freshCount) {
          count = parseInt(freshCount.textContent, 10);
          if (liveCount) {
            liveCount.textContent = freshCount.textContent;
          }
        }

        updateHeaderBubble(count);

        loadRecommendations();
      });
  }

  /**
   * sellingPlan is optional. Passing it converts the line to a subscription;
   * omitting it leaves any existing plan alone.
   */
  function changeLine(line, quantity, sellingPlan) {
    var payload = { line: line, quantity: quantity };

    // An empty string clears an existing plan; null means "leave it alone".
    // A plain truthiness check would swallow '' and silently do nothing.
    if (sellingPlan !== null && typeof sellingPlan !== 'undefined') {
      payload.selling_plan = sellingPlan;
    }

    return fetch(theme.routes.cartChange, {
      method: 'POST',
      credentials: 'same-origin',
      headers: JSON_HEADERS,
      body: JSON.stringify(payload)
    }).then(readCartResponse);
  }

  function addVariant(variantId) {
    return fetch(theme.routes.cartAdd, {
      method: 'POST',
      credentials: 'same-origin',
      headers: JSON_HEADERS,
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    }).then(readCartResponse);
  }

  function readCartResponse(response) {
    return response.json().then(function (data) {
      return { ok: response.ok, data: data };
    });
  }

  /**
   * Single path for every cart mutation: quantity, remove, subscribe and add.
   * Always refreshes, including on failure, so a rejected change resyncs the
   * drawer with what the cart actually contains rather than leaving a wrong
   * number on screen.Success is announced only when the mutation actually succeeded; a failure shows its reason in the drawer 
   * and suppresses the cart:updated event rather than emitting a null cart.
   */
    function mutate(request, focusKey) {
    if (busy) {
      return;
    }

    var cartData = null;
    var errorMessage = '';

    setBusy(true);
    setError('');

    return request()
      .then(function (result) {
        if (result.ok) {
          cartData = result.data;
        } else {
          errorMessage =
            (result.data && (result.data.description || result.data.message)) ||
            GENERIC_CART_ERROR;
        }
        return refresh();
      })
      .catch(function (error) {
        console.error('[en-cart-drawer]', error);

        if (!errorMessage) {
          errorMessage = GENERIC_CART_ERROR;
        }
      })
      .then(function () {
        setBusy(false);

        if (theme.EnCartDrawer && theme.EnCartDrawer.isOpen) {
          restoreFocusAndTrap(focusKey);
        }

        if (errorMessage) {
          setError(errorMessage);
          return;
        }

        var subtotalEl = document.querySelector('[data-cart-subtotal]');
        var subtotal = subtotalEl ? subtotalEl.textContent.replace(/\s+/g, ' ').trim() : '';
        announce(subtotal ? ('Cart updated. Subtotal ' + subtotal) : 'Cart updated.');

        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { cart: cartData }
        }));
      });
  }

  function restoreFocusAndTrap(focusKey) {
    armTrap(focusKey);
  }

  function announce(message) {
    var el = document.querySelector(SELECTORS.status);
    if (el) {
      el.textContent = message;
    }
  }

  /**
   * The error node is a permanently present role="alert" container. Injecting
   * text into it is what triggers the announcement, so it is never removed
   * from the accessibility tree; :empty collapses it visually instead.
   */
  function setError(message) {
    var el = document.querySelector(SELECTORS.error);
    if (el) {
      el.textContent = message || '';
    }
  }

  var trapState = {
    active: false,
    key: null,
    keydownHandler: null,
    focusinHandler: null
  };

  function drawerFocusables() {
    var drawerEl = getDrawer();
    if (!drawerEl) {
      return [];
    }
    var nodes = drawerEl.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex^="-"])'
    );
    return Array.prototype.filter.call(nodes, function (el) {
      return el.offsetParent !== null && el.getAttribute('aria-hidden') !== 'true';
    });
  }

  /**
   * A self-contained focus trap. Deliberately does NOT use Impulse's
   * theme.a11y.trapFocus: that helper only arms once a focusin lands on a
   * specific element, defers via its own timing, and competes with the drawer's
   * activeSource focus restore — the combination left focus on the cart icon no
   * matter what we did. This owns focus outright: it moves focus in on open, and
   * any focusin that escapes the drawer is pulled straight back.
   */
  function armTrap(focusKey) {
    var drawerEl = getDrawer();
    if (!drawerEl) {
      return;
    }

    trapState.key = focusKey || null;

    if (!trapState.active) {
      trapState.active = true;

      trapState.keydownHandler = function (evt) {
        if (evt.key !== 'Tab' && evt.keyCode !== 9) {
          return;
        }
        var focusables = drawerFocusables();
        if (!focusables.length) {
          return;
        }
        var first = focusables[0];
        var last = focusables[focusables.length - 1];
        var active = document.activeElement;

        if (evt.shiftKey) {
          if (active === first || !drawerEl.contains(active)) {
            evt.preventDefault();
            last.focus();
          }
        } else {
          if (active === last || !drawerEl.contains(active)) {
            evt.preventDefault();
            first.focus();
          }
        }
      };

      // Anything that pulls focus outside the open drawer — Impulse's own
      // activeSource restore, a stray click handler — is corrected here.
      trapState.focusinHandler = function (evt) {
        if (!trapState.active) {
          return;
        }
        if (drawerEl.contains(evt.target)) {
          return;
        }
        var focusables = drawerFocusables();
        if (focusables.length) {
          evt.stopPropagation();
          focusables[0].focus();
        }
      };

      document.addEventListener('keydown', trapState.keydownHandler, true);
      document.addEventListener('focusin', trapState.focusinHandler, true);
    }

    // Move focus in. rAF lets the panel finish opening; the focusin guard above
    // then defends the position against anything that fires afterwards.
    requestAnimationFrame(function () {
      var target = trapState.key ? drawerEl.querySelector(trapState.key) : null;
      if (!target || target.offsetParent === null) {
        target = drawerEl.querySelector('.js-drawer-close');
      }
      if (!target || target.offsetParent === null) {
        var focusables = drawerFocusables();
        target = focusables.length ? focusables[0] : null;
      }
      if (target && typeof target.focus === 'function') {
        target.focus();
      }
    });
  }

  function releaseTrap() {
    if (!trapState.active) {
      return;
    }
    trapState.active = false;
    document.removeEventListener('keydown', trapState.keydownHandler, true);
    document.removeEventListener('focusin', trapState.focusinHandler, true);
    trapState.keydownHandler = null;
    trapState.focusinHandler = null;
  }

  function openDrawer() {
    setError('');

    if (theme.EnCartDrawer) {
      theme.EnCartDrawer.open();
    }
    armTrap();
  }

  /* --- bindings ---------------------------------------------------------- */

  function init() {
    if (typeof window.theme === 'undefined' || typeof theme.Drawers === 'undefined') {
      return;
    }

    var drawerEl = getDrawer();
    if (!drawerEl) {
      return;
    }

    // Impulse suppressed its own drawer on the cart page; opening a drawer on
    // top of /cart is redundant, so match that behaviour.
    if (document.body.classList.contains('template-cart')) {
      return;
    }

    if (drawerEl.getAttribute('data-en-cd-init') === 'true') {
      return;
    }
    drawerEl.setAttribute('data-en-cd-init', 'true');

    theme.EnCartDrawer = new theme.Drawers('CartDrawer', 'cart');

    // Impulse arms trapFocus during the open transition, when the contents can
    // report no layout yet and the focusable list comes back empty. Re-arm after
    // its drawerOpen event, deferred to the next frame so layout exists.
    document.addEventListener('drawerOpen.CartDrawer', function () {
      armTrap();
    });

    // Impulse can close the drawer without going through our close handler
    // (Escape, click-outside). Release the trap whenever the open class leaves.
    new MutationObserver(function () {
      if (!drawerEl.classList.contains('drawer--is-open')) {
        releaseTrap();
      }
    }).observe(drawerEl, { attributes: true, attributeFilter: ['class'] });
  }

  /**
   * Every delegated handler below is bound to document so it survives the
   * [data-cart-contents] swaps, then scoped to inside the drawer so it can
   * never intercept a matching element elsewhere on the page.
   */
  function bindLineChanges() {
    document.addEventListener('click', function (evt) {
      if (!evt.target.closest) {
        return;
      }

      var trigger = evt.target.closest(SELECTORS.change);
      if (!trigger || !trigger.closest(SELECTORS.drawer)) {
        return;
      }

      evt.preventDefault();

      var change = parseChangeHref(trigger.getAttribute('href'));
      if (!change) {
        return;
      }

      var focusKey = trigger.matches('[data-cart-remove]') ? null : '[data-line="' + change.line + '"] ' + (trigger.matches('[data-qty-up]') ? '[data-qty-up]' : '[data-qty-down]');

      mutate(function () {
        return changeLine(change.line, change.quantity, null);
      }, focusKey);
    });
  }

  /**
   * Subscription upgrade. Quantity is read from the rendered line rather than
   * assumed, so upgrading a line with 3 units keeps all 3.
   */
  function bindSubscribe() {
    document.addEventListener('click', function (evt) {
      if (!evt.target.closest) {
        return;
      }

      var trigger = evt.target.closest(SELECTORS.subscribe);
      if (!trigger || !trigger.closest(SELECTORS.drawer)) {
        return;
      }

      evt.preventDefault();

      var line = parseInt(trigger.getAttribute('data-line'), 10);
      var sellingPlan = parseInt(trigger.getAttribute('data-selling-plan'), 10);
      if (isNaN(line) || isNaN(sellingPlan)) {
        return;
      }

      var quantity = 1;
      var item = trigger.closest(SELECTORS.item);
      var qtyEl = item ? item.querySelector(SELECTORS.qtyNum) : null;

      if (qtyEl) {
        var parsedQty = parseInt(qtyEl.textContent, 10);
        if (!isNaN(parsedQty) && parsedQty > 0) {
          quantity = parsedQty;
        }
      }

      mutate(function () {
        return changeLine(line, quantity, sellingPlan);
      }, '[data-line="' + line + '"] [data-cart-plan]');
    });
  }

  function bindRecommendationAdd() {
    document.addEventListener('click', function (evt) {
      if (!evt.target.closest) {
        return;
      }

      var trigger = evt.target.closest(SELECTORS.recAdd);
      if (!trigger || !trigger.closest(SELECTORS.drawer)) {
        return;
      }

      evt.preventDefault();

      var variantId = parseInt(trigger.getAttribute('data-variant-id'), 10);
      if (isNaN(variantId)) {
        return;
      }

      mutate(function () {
        return addVariant(variantId);
      }, null);
    });
  }

  function bindRecommendationArrows() {
    document.addEventListener('click', function (evt) {
      if (!evt.target.closest) {
        return;
      }

      var trigger = evt.target.closest(SELECTORS.recPrev + ', ' + SELECTORS.recNext);
      if (!trigger || !trigger.closest(SELECTORS.drawer)) {
        return;
      }

      evt.preventDefault();

      var container = trigger.closest(SELECTORS.rec);
      var track = container ? container.querySelector(SELECTORS.recTrack) : null;
      if (!track) {
        return;
      }

      var direction = trigger.matches(SELECTORS.recNext) ? 1 : -1;
      track.scrollLeft = track.scrollLeft + (cardStep(track) * direction);
    });
  }

  function lineQuantity(el) {
    var item = el.closest(SELECTORS.item);
    var qtyEl = item ? item.querySelector(SELECTORS.qtyNum) : null;
    if (!qtyEl) {
      return 1;
    }
    var parsed = parseInt(qtyEl.textContent, 10);
    return (!isNaN(parsed) && parsed > 0) ? parsed : 1;
  }

  function bindPlanChange() {
    document.addEventListener('click', function (evt) {
      if (!evt.target.closest) {
        return;
      }

      var trigger = evt.target.closest(SELECTORS.plan);
      if (!trigger || !trigger.closest(SELECTORS.drawer)) {
        return;
      }

      evt.preventDefault();

      // Already the selected frequency — no request needed.
      if (trigger.classList.contains('is-active')) {
        return;
      }

      var line = parseInt(trigger.getAttribute('data-line'), 10);
      var sellingPlan = parseInt(trigger.getAttribute('data-selling-plan'), 10);
      if (isNaN(line) || isNaN(sellingPlan)) {
        return;
      }

      mutate(function () {
        return changeLine(line, lineQuantity(trigger), sellingPlan);
      }, '[data-line="' + line + '"] [data-cart-plan]');
    });
  }

  function bindUnsubscribe() {
    document.addEventListener('click', function (evt) {
      if (!evt.target.closest) {
        return;
      }

      var trigger = evt.target.closest(SELECTORS.unsubscribe);
      if (!trigger || !trigger.closest(SELECTORS.drawer)) {
        return;
      }

      evt.preventDefault();

      var line = parseInt(trigger.getAttribute('data-line'), 10);
      if (isNaN(line)) {
        return;
      }

      mutate(function () {
        return changeLine(line, lineQuantity(trigger), '');
      }, '[data-line="' + line + '"] [data-cart-subscribe]');
    });
  }

  function bindCloseTriggers() {
    document.addEventListener('click', function (evt) {
      if (!evt.target.closest) {
        return;
      }

      var trigger = evt.target.closest(SELECTORS.close);
      if (!trigger || !theme.EnCartDrawer) {
        return;
      }

      evt.preventDefault();
      theme.EnCartDrawer.close();
      releaseTrap();
    });
  }

  function bindAddToCart() {
    document.addEventListener('ajaxProduct:added', function () {
      setBusy(true);
      openDrawer();

      refresh()
        .then(function () {
          setBusy(false);
        })
        .catch(function (error) {
          setBusy(false);

          // Never strand the customer after a successful add: fall back to the
          // cart page if the drawer cannot be refreshed.
          console.error('[en-cart-drawer]', error);
          window.location = theme.routes.cartPage;
        });
    });
  }

  function bindThemeEditor() {
    document.addEventListener('shopify:section:load', function (evt) {
      if (evt.detail && evt.detail.sectionId === SECTION_ID) {
        init();
        loadRecommendations();
      }
    });
  }

  function start() {
    init();
    bindLineChanges();
    bindSubscribe();
    bindPlanChange();
    bindUnsubscribe();
    bindRecommendationAdd();
    bindRecommendationArrows();
    bindCloseTriggers();
    bindAddToCart();
    bindThemeEditor();

    loadRecommendations();

    // Exposed for debugging.
    theme.enCartDrawerRefresh = refresh;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
